import { mdiCheck, mdiClose, mdiSwapHorizontal } from '@mdi/js';
import { SxProps } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { Chip, IChipProps } from 'common/components/Chip';
import { Icon } from 'common/components/Icon';
import { receivedCertificateEndDateNear } from 'common/utils/receivedCertificate';
import { Tooltip } from 'common/components/Tooltip';
import { withSxProp } from 'common/utils/props';
import { ICertificateValidationStatus } from 'graphql/types';

interface IProps {
    endDate?: string | null;
    isMandatory?: boolean;
    startDate?: string;
    sx?: SxProps;
    validationStatus?: ICertificateValidationStatus;
    withIcon?: boolean;
    onClick?(): void;
}

export const ReceivedCertificateProgressChip = ({
    endDate,
    isMandatory,
    startDate,
    sx,
    validationStatus,
    withIcon,
    onClick,
}: IProps) => {
    const [translate] = useTranslation();

    const isExpired = !endDate ? false : new Date(endDate) < new Date();
    const isEndDateNear = !!endDate && receivedCertificateEndDateNear(endDate);

    // Mandatory with no validation/denied AND no valid endDate: red chip with cross icon
    if (
        !!isMandatory &&
        (!validationStatus ||
            validationStatus === ICertificateValidationStatus.Denied) &&
        (!endDate || isExpired)
    ) {
        const mandatoryTooltip = isExpired
            ? translate('receivedCertificateProgressChip.expired')
            : translate('receivedCertificateProgressChip.actionRequired');

        return (
            <Tooltip title={mandatoryTooltip}>
                <Chip
                    aria-label={`${translate('score')} ${translate('notFulfilled')}`}
                    color="error"
                    icon={
                        withIcon ? (
                            <Icon path={mdiClose} size="2rem" />
                        ) : undefined
                    }
                    sx={[...withSxProp(sx)]}
                    tabIndex={!!onClick ? 0 : -1}
                    onClick={onClick}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') onClick?.();
                    }}
                />
            </Tooltip>
        );
    }

    // Non-mandatory expired items: grey chip (red date text is shown by the caller)
    if (isMandatory === false && isExpired) {
        return (
            <Chip
                sx={[...withSxProp(sx), { backgroundColor: 'grey.200' }]}
                tabIndex={!!onClick ? 0 : -1}
                onClick={onClick}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') onClick?.();
                }}
            />
        );
    }

    let color: IChipProps['color'] | undefined;
    let style: IChipProps['sx'] = {};
    let toolTipTitle: string | undefined;

    if (isExpired) {
        color = 'error';
        toolTipTitle = translate('receivedCertificateProgressChip.expired');
    } else if (!!validationStatus) {
        if (validationStatus === ICertificateValidationStatus.Pending) {
            color = 'validation';
            toolTipTitle = translate(
                'receivedCertificateProgressChip.validationPending'
            );
        } else if (validationStatus === ICertificateValidationStatus.Denied) {
            color = 'error';
            toolTipTitle = translate(
                'receivedCertificateProgressChip.validationRejected'
            );
        } else if (validationStatus === ICertificateValidationStatus.Approved) {
            if (isEndDateNear) {
                color = 'warning';
                toolTipTitle = translate(
                    'receivedCertificateProgressChip.nearExpiry'
                );
            } else {
                color = 'success';
                toolTipTitle = translate(
                    'receivedCertificateProgressChip.validationApproved'
                );
            }
        }
    } else if (startDate && new Date(startDate) > new Date()) {
        color = 'success';
        toolTipTitle = translate('receivedCertificateProgressChip.notYetValid');
        style = { opacity: 0.3 };
    } else if (isEndDateNear) {
        color = 'warning';
        toolTipTitle = translate('receivedCertificateProgressChip.nearExpiry');
    } else if (endDate) {
        color = 'success';
        toolTipTitle = translate('receivedCertificateProgressChip.valid');
    }

    // No positive evidence of validity: grey chip without icon
    if (!color) {
        return (
            <Chip
                sx={[...withSxProp(sx), { backgroundColor: 'grey.200' }]}
                tabIndex={!!onClick ? 0 : -1}
                onClick={onClick}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') onClick?.();
                }}
            />
        );
    }

    let icon: React.ReactElement | undefined;

    if (color === 'error') {
        icon = <Icon path={mdiClose} size="2rem" />;
    } else if (color === 'validation') {
        icon = <Icon path={mdiSwapHorizontal} size="2rem" />;
    } else {
        icon = <Icon path={mdiCheck} size="2rem" />;
    }

    const ariaLabel = !!onClick
        ? `${translate('progress')} ${toolTipTitle}`
        : undefined;

    return (
        <Tooltip title={toolTipTitle}>
            <Chip
                aria-label={ariaLabel}
                color={color}
                icon={withIcon ? icon : undefined}
                sx={[...withSxProp(sx), style]}
                tabIndex={!!onClick ? 0 : -1}
                onClick={onClick}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') onClick?.();
                }}
            />
        </Tooltip>
    );
};
